import Vue from 'vue'
import store from '@/store'
import Router from 'vue-router'
import Multiguard from 'vue-router-multiguard'
import LocalStore from './lib/localStore'
import { jwtDecode } from 'jwt-decode'
import { isJwtExpired, useWatchEffect } from './lib/utils'
import appAxios from './extra-functions/axios.extra'

// const localStore = new Store()


const localStore = new LocalStore()
// import multiguard from 'vue-router-multiguard'
Vue.use(Router)

const [watchEffect] = useWatchEffect()

const verifyLoggedIn = async (to, from, next) => {
  const loggedIn = await reLogin()
  if (!loggedIn) {
    next({ name: 'Login' })
  }
  watchEffect(async (onCleanup) => {
    try {
      const controller = new AbortController()
      onCleanup(() => {
        controller.abort()
      })
      const { data: permissions } = await appAxios(store.state.auth.token).get('/admin/users/own-permissions', {
        signal: controller.signal,
      })
      store.dispatch('auth/setUserPermissions', permissions)
    } catch (e) {
      console.error(e)
    }
  }, 'ROUTER_FETCH_OWN_PERMISSIONS')
  next()
}

const verifyAdmin = (to, from, next) => {
  const adminCode = store.state.auth.user.admin
  if (!adminCode) {
    next({ name: 'Dashboard' })
  }
  next()
}

/**
 * Description
 * @param {string|string[]} permissions
 * @param {import('vue-router').NavigationGuard} callback=null
 * @returns {import('vue-router').NavigationGuard}
 */
const verifyPermission = (permissions, callback = null) => async (to, from, next) => {
  const perms = Array.isArray(permissions) ? permissions : [permissions]
  const userPerms = store.state.auth?.userPermissions
  const hasAll = perms.every(p => userPerms?.has(p))
  if (!hasAll) return next(false)
  const callbackResult = callback?.(to, from, next) ?? null
  if (callbackResult) return callbackResult
  return next()
}

const reLogin = async () => {
  try {
    const loggedIn = store.state.auth.loggedIn
    if (loggedIn) return true
    const data = localStore.get('auth', null)
    const { expired } = isJwtExpired(data?.token)
    if (expired && data?.token) {
      store.dispatch('auth/logout')
      localStore.delete('auth')
      return false
    }
    if (data) {
      store.dispatch('auth/setToken', data?.token)
      store.dispatch('auth/setUser', data?.user)
      store.dispatch('branchLocation/setBranchList', data?.stores ?? [])
      store.dispatch('auth/setLogin')
      store.dispatch('settings/setStoreId', data?.storeId ?? '')
      const { data: permissions } = await appAxios(data?.token).get('/admin/users/own-permissions')
      store.dispatch('auth/setUserPermissions', permissions)
      return true
    }
    return false
  } catch (e) {
    return false
  }
}

const verifyNotLoggedIn = async (to, from, next) => {
  try {
    const loggedIn = await reLogin()
    if (loggedIn) return next({ name: 'Dashboard' })
    next()
  } catch (e) {
    console.error(e)
    localStore.delete('auth')
    next({ name: 'Login' })
  }
}

export const routeNames = [
  { routeName: 'Admin.Users', alias: 'Admin' },
  { routeName: 'Dashboard', alias: 'Dashboard V2' },
  { routeName: 'Inventories.Transfer', alias: 'Inventory - Transfer' },
  { routeName: 'Inventories.TransferReceived', alias: 'Inventory - Receive' },
  { routeName: 'Inventories.Recieved', alias: 'Inventory - Purchases' },
  { routeName: 'Inventories.Sale', alias: 'Inventory - Sold' },
  { routeName: 'Inventories.Lost', alias: 'Inventory - Lost' },
  { routeName: 'Expenses', alias: 'Manage Expenses' },
  { routeName: 'Stocks', alias: 'Manage Stock' },
  { routeName: 'Suppliers', alias: 'Manage Supplier' },
  { routeName: 'StoreBalances', alias: 'Store Balance' },
  { routeName: 'InventoryMonitoring', alias: 'Inventory Monitoring' },
  { routeName: 'Export', alias: 'Export' },
  { routeName: 'Consumers', alias: 'Manage Consumer' },
]

export default new Router({
  mode: 'hash',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '/',
      component: () => import('@/views/dashboard/Index'),
      beforeEnter: verifyLoggedIn,
      children: [
        // Dashboard
        // {
        //   name: 'Dashboard',
        //   path: '',
        //   component: () => import('@/views/dashboard/Dashboard'),
        // },
        {
          name: 'Dashboard',
          path: '',
          component: () => import('@/views/dashboard/DashboardV2'),
        },
        {
          path: 'promotions',
          component: () => import('@/views/dashboard/components/promotions/Page.vue'), // wrapper Page.vue
          beforeEnter: verifyPermission('promotions:view'),
          children: [
            {
              name: 'Promotions',
              path: '',
              component: () => import('@/views/dashboard/components/promotions/Index.vue'),
            },
            {
              name: 'Promotions.Create',
              path: 'create',
              component: () => import('@/views/dashboard/components/promotions/Create.vue'),
            },
            {
              name: 'Promotions.Edit',
              path: ':id/edit',
              component: () => import('@/views/dashboard/components/promotions/Edit.vue'),
            },
          ],
        },
        {
          name: 'Printouts.SalesReport',
          path: 'printouts/sales-report',
          component: () => import('@/views/dashboard/components/printouts/SalesReport'),
          beforeEnter: verifyPermission('sales:insight_reports', (to, from) => {
            to.meta.backLink = {
              path: from.fullPath,
              query: from.query,
            }
          }),
        },
        {
          name: 'InventoryMonitoring',
          path: 'inventory-monitoring',
          component: () => import('@/views/dashboard/pages/InventoryMonitoring'),
          beforeEnter: verifyPermission('inventory_monitoring:view'),
        },
        {
          path: 'stores',
          component: () => import('@/views/dashboard/pages/Stores'),
          children: [
            {
              name: 'Stores.Index',
              path: '',
              component: () => import('@/views/dashboard/components/store-summaries/Index'),
            },
            {
              name: 'Stores.Show',
              path: ':storeId',
              component: () => import('@/views/dashboard/components/store-summaries/Show'),
            },
          ],
        },
        {
          name: 'StoreBalances',
          path: 'store-balances',
          component: () => import('@/views/dashboard/pages/StoreBalance'),
          beforeEnter: verifyPermission('store_balances:view'),
        },
        {
          path: 'admin',
          component: () => import('@/views/dashboard/pages/Admin'),
          // beforeEnter: multiguard([
          //   verifyLoggedIn,
          //   verifyAdmin,
          // ]),
          children: [
            {
              name: 'Admin.Dashboard',
              path: 'main',
              component: () => import('@/views/dashboard/pages/admin/Dashboard'),
              beforeEnter: Multiguard([
                verifyLoggedIn,
                verifyAdmin,
              ]),
            },
            {
              name: 'Admin.Users',
              path: '',
              component: () => import('@/views/dashboard/pages/admin/User'),
              beforeEnter: Multiguard([
                verifyLoggedIn,
                verifyAdmin,
              ]),
            },
          ],
        },
        // Login
        // Page
        {
          name: 'WholeSales',
          path: 'whole-sales',
          component: () => import('@/views/dashboard/components/whole-sales/Page'),
          beforeEnter: verifyPermission('whole_sales:view'),
        },
        {
          name: 'Expenses',
          path: 'expenses',
          component: () => import('@/views/dashboard/pages/Expenses'),
          beforeEnter: verifyPermission('expenses:view'),
        },
        {
          name: 'Stocks',
          path: 'stocks',
          component: () => import('@/views/dashboard/pages/Stocks'),
          beforeEnter: verifyPermission('stocks:view'),
        },
        {
          name: 'Suppliers',
          path: 'suppliers',
          component: () => import('@/views/dashboard/pages/Suppliers'),
          beforeEnter: verifyPermission('suppliers:view'),
        },
        {
          name: 'Supplier.Products',
          path: 'suppliers/:supplier/products',
          component: () => import('@/views/dashboard/pages/SupplierProducts'),
          beforeEnter: verifyPermission('suppliers:view'),
        },
        {
          name: 'Inventories.Lost',
          path: 'inventories/lost',
          component: () => import('@/views/dashboard/pages/LostInventories'),
          beforeEnter: verifyPermission('lost:view'),
        },
        {
          name: 'Inventories.Recieved',
          path: 'inventories/recieved',
          component: () => import('@/views/dashboard/pages/RecievedInventories'),
          beforeEnter: verifyPermission('purchases:view'),
        },
        {
          name: 'Inventories.Sale',
          path: 'inventories/sale',
          component: () => import('@/views/dashboard/pages/SaleInventories'),
          beforeEnter: verifyPermission('sales:view'),
        },
        {
          name: 'Inventories.Transfer',
          path: 'inventories/transfer',
          component: () => import('@/views/dashboard/pages/TransferInventory'),
          beforeEnter: verifyPermission('store_transfers:view'),
        },

        {
          name: 'Inventories.Breakdown',
          path: 'inventories/breakdown',
          component: () => import('@/views/dashboard/pages/InventoryBreakdown'), // <-- this is the new route
          beforeEnter: verifyPermission('inventory_breakdown:view'),
        },
        {
          name: 'Inventories.TransferReceived',
          path: 'inventories/transfer-receive',
          component: () => import('@/views/dashboard/pages/ReceiveTransferInventory'),
          beforeEnter: verifyPermission('store_receives:view'),
        },
        // {
        //   name: 'Warehouse',
        //   path: 'warehouse',
        //   component: () => import('@/views/dashboard/pages/Warehouse'),
        // },
        {
          name: 'PromotionStocks',
          path: 'promotion-stocks',
          component: () => import('@/views/dashboard/components/promotion-stocks/Page.vue'),
          beforeEnter: verifyPermission(['promotions:view', 'stocks:view']),
        },
        {
          name: 'Export',
          path: 'export',
          component: () => import('@/views/dashboard/pages/Export'),
          beforeEnter: verifyPermission('exports:view'),
        },
        {
          path: 'stock-brand-types',
          component: () => import('@/views/dashboard/components/stock-brand-types/Page.vue'),
          beforeEnter: verifyPermission('stock_brand_types:view'),
          children: [
            {
              name: 'StockBrandTypes.Index',
              path: '',
              component: () => import('@/views/dashboard/components/stock-brand-types/Index.vue'),
            },
            {
              name: 'StockBrandTypes.Create',
              path: 'create',
              component: () => import('@/views/dashboard/components/stock-brand-types/Create.vue'),
            },
            {
              name: 'StockBrandTypes.Edit',
              path: ':id/edit',
              component: () => import('@/views/dashboard/components/stock-brand-types/Edit.vue'),
            },
          ],
        },
        {
          name: 'Consumer.TicketRequests',
          path: 'consumer-ticket-requests',
          component: () => import('@/views/dashboard/components/consumers/v2/ticket-requests/Index.vue'),
        },
        {
          path: 'consumers',
          component: () => import('@/views/dashboard/components/consumers/v2/Page'),
          beforeEnter: verifyPermission('consumers:view'),
          children: [
            // {
            //   name: 'Consumers',
            //   path: '',
            //   component: () => import('@/views/dashboard/components/consumers/Page'),
            // },
            {
              name: 'Consumers',
              path: '',
              component: () => import('@/views/dashboard/components/consumers/v2/Index'),
            },
            {
              name: 'Consumers.Transactions',
              path: ':consumer/transactions',
              component: () => import('@/views/dashboard/components/consumers/v2/transactions/Index'),
            },
            {
              name: 'Consumer.Loans',
              path: ':consumer/loans',
              component: () => import('@/views/dashboard/components/consumer-loans/Page'),
              beforeEnter: verifyPermission('consumer_loans:view'),
            },
            {
              name: 'Consumer.Loan.Transactions',
              path: ':consumer/loans/:consumerLoan/transactions',
              component: () => import('@/views/dashboard/components/consumer-loan-transactions/Page'),
              beforeEnter: verifyPermission('consumer_loans:view'),
            },
            {
              name: 'Consumer.Loan.Inventories',
              path: ':consumer/loans/:consumerLoan/inventories',
              component: () => import('@/views/dashboard/components/consumer-loan-inventories/Page'),
              beforeEnter: verifyPermission('consumer_loans:view'),
            },
          ],
        },
      ],
    },
    {
      path: '/public',
      component: () => import('@/views/public/Index'),
      children: [
        {
          name: 'Login',
          path: 'login',
          component: () => import('@/views/public/pages/Login'),
          beforeEnter: verifyNotLoggedIn,
        },
        // {
        //   name: 'Settings',
        //   path: 'settings',
        //   component: () => import('@/views/public/pages/Settings'),
        //   beforeEnter: verifyNotLoggedIn,
        // },
      ],
    },
  ],
})
